// frontend/src/components/marketplace/OrderTimeline.tsx
// Visual timeline showing order status progression

import React from 'react';
import {
  Clock,
  CheckCircle,
  Package,
  Truck,
  Home,
  XCircle,
  RefreshCw,
  AlertCircle,
} from 'lucide-react';
import { format } from 'date-fns';
import type { Order, OrderStatus } from '../types/marketplace';

interface OrderTimelineProps {
  order: Order;
  className?: string;
}

interface TimelineStep {
  status: OrderStatus;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  description: string;
}

const TIMELINE_STEPS: TimelineStep[] = [
  {
    status: 'pending',
    label: 'Order Placed',
    icon: Clock,
    description: 'Your order has been submitted',
  },
  {
    status: 'confirmed',
    label: 'Confirmed',
    icon: CheckCircle,
    description: 'Vendor has confirmed your order',
  },
  {
    status: 'processing',
    label: 'Processing',
    icon: Package,
    description: 'Order is being prepared',
  },
  {
    status: 'shipped',
    label: 'Shipped',
    icon: Truck,
    description: 'Order is on its way',
  },
  {
    status: 'delivered',
    label: 'Delivered',
    icon: Home,
    description: 'Order has been delivered',
  },
];

const STATUS_ORDER: Record<OrderStatus, number> = {
  pending: 0,
  confirmed: 1,
  processing: 2,
  shipped: 3,
  delivered: 4,
  cancelled: -1,
  refunded: -2,
};

export const OrderTimeline: React.FC<OrderTimelineProps> = ({
  order,
  className = '',
}) => {
  const isCancelled = order.status === 'cancelled';
  const isRefunded = order.status === 'refunded';
  const currentStepIndex = STATUS_ORDER[order.status];

  const getStepStatus = (stepIndex: number): 'completed' | 'current' | 'upcoming' => {
    if (isCancelled || isRefunded) {
      return stepIndex <= currentStepIndex ? 'completed' : 'upcoming';
    }
    if (stepIndex < currentStepIndex) return 'completed';
    if (stepIndex === currentStepIndex) return 'current';
    return 'upcoming';
  };

  const getDateForStep = (step: TimelineStep): string | null => {
    switch (step.status) {
      case 'pending':
        return order.orderedAt ? format(new Date(order.orderedAt), 'MMM d, yyyy h:mm a') : null;
      case 'confirmed':
        return order.confirmedAt ? format(new Date(order.confirmedAt), 'MMM d, yyyy h:mm a') : null;
      case 'shipped':
        return order.shippedAt ? format(new Date(order.shippedAt), 'MMM d, yyyy h:mm a') : null;
      case 'delivered':
        return order.deliveredAt ? format(new Date(order.deliveredAt), 'MMM d, yyyy h:mm a') : null;
      default:
        return null;
    }
  };

  // Render cancelled/refunded state
  if (isCancelled || isRefunded) {
    return (
      <div className={`bg-white rounded-xl p-6 ${className}`}>
        <h3 className="font-display text-lg font-semibold text-navy mb-4">
          Order Status
        </h3>
        
        <div className={`flex items-center gap-4 p-4 rounded-lg ${
          isCancelled ? 'bg-red-50' : 'bg-amber-50'
        }`}>
          <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
            isCancelled ? 'bg-red-100' : 'bg-amber-100'
          }`}>
            {isCancelled ? (
              <XCircle className="w-6 h-6 text-red-600" />
            ) : (
              <RefreshCw className="w-6 h-6 text-amber-600" />
            )}
          </div>
          <div>
            <p className={`font-semibold ${isCancelled ? 'text-red-700' : 'text-amber-700'}`}>
              Order {isCancelled ? 'Cancelled' : 'Refunded'}
            </p>
            <p className={`text-sm ${isCancelled ? 'text-red-600' : 'text-amber-600'}`}>
              {isCancelled 
                ? 'This order has been cancelled and will not be fulfilled.'
                : 'This order has been refunded. Check your payment method for the refund.'}
            </p>
          </div>
        </div>

        {/* Show what was completed before cancellation */}
        {currentStepIndex >= 0 && (
          <div className="mt-6">
            <p className="text-sm text-warm-gray mb-3">Order history before {isCancelled ? 'cancellation' : 'refund'}:</p>
            <div className="space-y-3">
              {TIMELINE_STEPS.slice(0, currentStepIndex + 1).map((step, index) => {
                const date = getDateForStep(step);
                return (
                  <div key={step.status} className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center">
                      <CheckCircle className="w-4 h-4 text-gray-500" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm text-gray-600">{step.label}</p>
                      {date && (
                        <p className="text-xs text-gray-400">{date}</p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={`bg-white rounded-xl p-6 ${className}`}>
      <h3 className="font-display text-lg font-semibold text-navy mb-6">
        Order Status
      </h3>

      {/* Timeline */}
      <div className="relative">
        {TIMELINE_STEPS.map((step, index) => {
          const status = getStepStatus(index);
          const Icon = step.icon;
          const date = getDateForStep(step);
          const isLast = index === TIMELINE_STEPS.length - 1;

          return (
            <div key={step.status} className="relative flex gap-4">
              {/* Vertical line */}
              {!isLast && (
                <div
                  className={`absolute left-5 top-10 w-0.5 h-full -ml-px ${
                    status === 'completed'
                      ? 'bg-sage'
                      : status === 'current'
                      ? 'bg-gradient-to-b from-sage to-gray-200'
                      : 'bg-gray-200'
                  }`}
                />
              )}

              {/* Icon */}
              <div
                className={`relative z-10 w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 transition-all ${
                  status === 'completed'
                    ? 'bg-sage text-white'
                    : status === 'current'
                    ? 'bg-sage text-white ring-4 ring-sage/20'
                    : 'bg-gray-100 text-gray-400'
                }`}
              >
                <Icon className="w-5 h-5" />
              </div>

              {/* Content */}
              <div className={`pb-8 ${isLast ? 'pb-0' : ''}`}>
                <div className="flex items-center gap-2">
                  <p
                    className={`font-medium ${
                      status === 'upcoming' ? 'text-gray-400' : 'text-navy'
                    }`}
                  >
                    {step.label}
                  </p>
                  {status === 'current' && (
                    <span className="px-2 py-0.5 bg-sage/10 text-sage text-xs font-medium rounded-full">
                      Current
                    </span>
                  )}
                </div>
                <p
                  className={`text-sm mt-0.5 ${
                    status === 'upcoming' ? 'text-gray-300' : 'text-warm-gray'
                  }`}
                >
                  {step.description}
                </p>
                {date && (
                  <p className="text-xs text-sage mt-1">{date}</p>
                )}

                {/* Tracking info for shipped status */}
                {step.status === 'shipped' && status !== 'upcoming' && order.trackingNumber && (
                  <div className="mt-2 p-3 bg-cream rounded-lg">
                    <p className="text-xs text-warm-gray">Tracking Number:</p>
                    <p className="text-sm font-mono text-navy">{order.trackingNumber}</p>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Estimated delivery for non-delivered orders */}
      {order.status !== 'delivered' && order.status !== 'cancelled' && order.status !== 'refunded' && (
        <div className="mt-6 p-4 bg-cream rounded-lg">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-sage flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-navy">Estimated Delivery</p>
              <p className="text-sm text-warm-gray">
                Based on the current status, your order should arrive within 5-7 business days.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Compact horizontal timeline variant
interface OrderTimelineCompactProps {
  status: OrderStatus;
  className?: string;
}

export const OrderTimelineCompact: React.FC<OrderTimelineCompactProps> = ({
  status,
  className = '',
}) => {
  const currentStepIndex = STATUS_ORDER[status];
  const isCancelled = status === 'cancelled';
  const isRefunded = status === 'refunded';

  if (isCancelled || isRefunded) {
    return (
      <div className={`flex items-center gap-2 ${className}`}>
        <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
          isCancelled ? 'bg-red-100' : 'bg-amber-100'
        }`}>
          {isCancelled ? (
            <XCircle className="w-4 h-4 text-red-600" />
          ) : (
            <RefreshCw className="w-4 h-4 text-amber-600" />
          )}
        </div>
        <span className={`text-sm font-medium ${
          isCancelled ? 'text-red-600' : 'text-amber-600'
        }`}>
          {isCancelled ? 'Cancelled' : 'Refunded'}
        </span>
      </div>
    );
  }

  return (
    <div className={`flex items-center gap-1 ${className}`}>
      {TIMELINE_STEPS.map((step, index) => {
        const isCompleted = index <= currentStepIndex;
        const isCurrent = index === currentStepIndex;
        const Icon = step.icon;

        return (
          <React.Fragment key={step.status}>
            <div
              className={`w-7 h-7 rounded-full flex items-center justify-center transition-colors ${
                isCompleted
                  ? isCurrent
                    ? 'bg-sage text-white'
                    : 'bg-sage/20 text-sage'
                  : 'bg-gray-100 text-gray-300'
              }`}
              title={step.label}
            >
              <Icon className="w-3.5 h-3.5" />
            </div>
            {index < TIMELINE_STEPS.length - 1 && (
              <div
                className={`w-4 h-0.5 ${
                  index < currentStepIndex ? 'bg-sage' : 'bg-gray-200'
                }`}
              />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

export default OrderTimeline;
